// Phase 2: React Query hook for focus-aware report context fetching
// Provides caching, background updates, and loading states

import { useQuery } from '@tanstack/react-query';
import type { 
  ReportFocus, 
  ReportContextRequest, 
  ReportContextResponse 
} from '../../shared/types/reports';

// ============================================================================
// useReportContext Hook
// ============================================================================

export interface UseReportContextOptions {
  focus: ReportFocus;
  entityId: string | null;
  startDate: string;
  endDate: string;
  enabled?: boolean; // Only fetch when true
}

export function useReportContext(options: UseReportContextOptions) {
  const { focus, entityId, startDate, endDate, enabled = true } = options;

  return useQuery({
    queryKey: ['reportContext', focus, entityId, startDate, endDate],
    queryFn: async (): Promise<ReportContextResponse> => {
      if (!entityId) {
        throw new Error('Entity ID is required');
      }

      const params = new URLSearchParams({
        focus,
        entityId,
        startDate,
        endDate,
      });

      const response = await fetch(`/api/reports/context?${params}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to fetch report context');
      }

      return response.json();
    },
    enabled: enabled && !!entityId, // Only fetch when enabled and entityId exists
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

// ============================================================================
// useTemplates Hook
// ============================================================================

export function useTemplates(userId?: string) {
  return useQuery({
    queryKey: ['reportTemplates', userId],
    queryFn: async () => {
      const params = userId ? `?userId=${userId}` : '';
      const response = await fetch(`/api/reports/templates${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      return data.templates || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

// ============================================================================
// useApplyTemplate Hook
// ============================================================================

export function useApplyTemplate() {
  return async (templateId: string) => {
    const response = await fetch(`/api/reports/templates/${templateId}/apply`, {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to apply template');
    }

    return response.json();
  };
}

// ============================================================================
// useGenerateNarrative Hook
// ============================================================================

export function useGenerateNarrative() {
  return async (request: any) => {
    const response = await fetch('/api/reports/generate-narrative', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to generate narrative');
    }

    return response.json();
  };
}

// ============================================================================
// useSaveReport Hook
// ============================================================================

export function useSaveReport() {
  return async (request: any) => {
    const response = await fetch('/api/reports/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to save report');
    }

    return response.json();
  };
}
